import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { initialState, type ModelState } from '@/model/state';
import type { Patch } from '@/model/ctx';
import { SCREEN_MODE } from '@/model/nav';
import { SCREEN_BY_PATH } from '@/app/routes';
import { loadPersisted, persistedChanged, savePersisted } from './persist';

/** Deep links: the store starts on the screen the URL names (the reference always started on the dashboard). */
function fromLocation(): Partial<ModelState> {
  if (typeof window === 'undefined') return {};
  const id = SCREEN_BY_PATH[window.location.pathname];
  return id ? { screen: id, mode: SCREEN_MODE[id] ?? initialState.mode } : {};
}
/** The reference component's `state` + `setState`, as a zustand store. Screens never touch it directly — the model does. */
export const modelStore = createStore<ModelState>()(() => ({
  ...initialState,
  // The workspace the trader left behind — instrument, favourites, timeframe, theme. Applied under the deep link,
  // so a URL naming a screen still wins: the address bar is a more explicit instruction than last time's habit.
  ...loadPersisted(),
  ...fromLocation(),
}));

// Written back on change rather than on an interval or on unload: `beforeunload` is not reliably delivered on
// mobile, and a tab closed by the OS would take the last change with it.
modelStore.subscribe((next, prev) => {
  if (persistedChanged(prev, next)) savePersisted(next);
});
export function setModel(patch: Patch): void {
  modelStore.setState((s) => (typeof patch === 'function' ? patch(s) : patch));
}
export function useModelState(): ModelState {
  return useStore(modelStore);
}
